// src/features/live/LiveChannelCard.tsx — a "Live channels" row card. Links to
// the detail route. Mirrors src/features/videos/VideoCard.tsx, swapping the
// duration badge for a LIVE badge + a viewer-count line.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { ThumbBadge } from '@/components/GeneratedThumb/ThumbBadge';
import { formatViewers, LIVE_BADGE_BG } from './meta';
import type { LiveChannel, LiveCreator } from '@/core/models/live';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  text-decoration: none;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  transition: transform 120ms ease, border-color 120ms ease;

`;

const ThumbWrap = styled.div`
  position: relative;
  height: 120px;
`;

const LiveTag = styled(ThumbBadge)`
  background: ${LIVE_BADGE_BG};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 700;
`;

const Body = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface LiveChannelCardProps {
  channel: LiveChannel;
  creator?: LiveCreator;
}

export function LiveChannelCard({ channel, creator }: LiveChannelCardProps) {
  return (
    <Card to={`/live/${channel.id}`} aria-label={`${channel.title}, live, ${formatViewers(channel.viewerCount)}`}>
      <ThumbWrap>
        <GeneratedThumb seedKey={channel.id} fill live />
        <LiveTag $corner="tl">Live</LiveTag>
        <ThumbBadge $corner="br">{formatViewers(channel.viewerCount)}</ThumbBadge>
      </ThumbWrap>
      <Body>
        <Title>{channel.title}</Title>
        {creator && <Meta>{creator.name}</Meta>}
      </Body>
    </Card>
  );
}
